"use client";

import { useCallback } from "react";
import { useSearchParams } from "next/navigation";
import { toast } from "sonner";
import z from "zod";

import { updateDraftCurrentStepAction } from "@/app/lib/actions/cat/draft-current-step";
import { useStep } from "@/hooks/use-step-handler";
import type { VehicleAssignmentStepDefinition } from "@/lib/cat/vehicle-assignment-step-definitions";
import { deserializeURLSearchParams, validateParams } from "@/lib/utils";
import { useCurrentDraftStore } from "@/stores/selected-vehicle-store";

const draftIdSchema = z.object({
  draftId: z.preprocess(
    (val) => (val ? Number(val) : undefined),
    z.number().int().positive().optional(),
  ),
});

export function useRetailDraftStepNavigation(
  visibleDefinitions: VehicleAssignmentStepDefinition[],
) {
  const searchParams = useSearchParams();
  const { handleStepChange, step } = useStep();
  const currentDraft = useCurrentDraftStore((state) => state.currentDraft);
  const setCurrentDraft = useCurrentDraftStore(
    (state) => state.setCurrentDraft,
  );

  const { draftId: draftIdFromUrl } = validateParams(
    draftIdSchema,
    deserializeURLSearchParams(searchParams.toString()),
  );

  const navigateToStep = useCallback(
    (targetStep: number, options?: { persist?: boolean }) => {
      handleStepChange(targetStep);

      if (options?.persist === false) {
        return;
      }

      const draftId = currentDraft?.id ?? draftIdFromUrl;
      const stepId = visibleDefinitions[targetStep - 1]?.id;
      if (!draftId || !stepId) {
        return;
      }

      void updateDraftCurrentStepAction({ draftId, stepId }).then((result) => {
        if (result.success && result.data) {
          setCurrentDraft(result.data);
          return;
        }

        if (!result.success && result.message) {
          toast.error(result.message);
        }
      });
    },
    [
      currentDraft?.id,
      draftIdFromUrl,
      handleStepChange,
      setCurrentDraft,
      visibleDefinitions,
    ],
  );

  const goToPreviousStep = useCallback(
    (targetStep: number) => {
      navigateToStep(targetStep);
    },
    [navigateToStep],
  );

  return { goToPreviousStep, handleStepChange, navigateToStep, step };
}
